import {
	OTHER_EVENT_TYPE,
	otherEventTypeOption,
	type EventTypeOption,
	type PackageFormData,
	type Step,
} from "./types";
import {
	constantContactStep,
	constantDocumentsStep,
	constantEntryFlowCopy,
	constantEventDetailsStep,
	constantSteps,
	constantVenueStep,
} from "./profile-constants";

// The inquiry-profile schema: the InquiryProfileConfig type family and the
// runtime parser that validates a tenant-owned DB row into one. The
// app-constant sections it composes into every parsed profile live in
// profile-constants.ts; the live_sound reference literal is a test fixture
// (web/tests/__fixtures__/live-sound-profile.ts), copied from live_sound's
// seed — no production path reads it.

export type InquiryProfileKey = string;

export type BooleanPackageField = {
  [K in keyof PackageFormData]-?: NonNullable<PackageFormData[K]> extends boolean
    ? K
    : never;
}[keyof PackageFormData];

export interface InquiryStepConfig {
  id: Step;
  title: string;
  progressLabel: string;
}

export interface InquiryFieldConfig<K extends keyof PackageFormData> {
  field: K;
  label: string;
  optional?: boolean;
  hint?: string;
  placeholder?: string;
}

export interface InquiryScaleFieldConfig
  extends InquiryFieldConfig<"attendance"> {
  emptyMessage: string;
  invalidMessage: string;
}

/**
 * A checkbox bound to a flat PackageFormData boolean. Only the venue step's
 * app-constant checkboxes use this — content questions are all pack
 * questions, keyed by id into `profileAnswers`.
 */
export interface InquiryCheckboxQuestionConfig<
  K extends BooleanPackageField = BooleanPackageField,
> {
  field: K;
  label: string;
  description?: string;
}

export type InquiryPackQuestionType =
  | "checkbox"
  | "integer"
  | "text"
  | "textarea"
  | "select";

/**
 * One condition a question waits on: a core answer (`indoorOutdoor`) or the
 * answer to an earlier question on the same list, named by its id. A
 * checkbox's answer is "yes" or "no"; a select's is one of its options.
 * Every condition on a question must hold for it to be asked (see
 * `activeQuestions` in active-questions.ts).
 */
export interface InquiryPackQuestionCondition {
  field: string;
  equals: string;
}

/** The one core answer a question may wait on. */
export const CORE_CONDITION_FIELD = "indoorOutdoor";

/** The most conditions one question may carry (`profile.MaxConditions`). */
export const MAX_PACK_CONDITIONS = 4;

/**
 * One question in a profile's data-driven content-step pack. Unlike
 * InquiryCheckboxQuestionConfig these are not bound to PackageFormData
 * fields — answers collect into `formData.profileAnswers` keyed by `id`.
 * Pack-specific behaviour remains data-driven: questions can be required,
 * conditional on the shared environment answer, and carry customer guidance.
 */
export interface InquiryPackQuestionConfig {
  id: string;
  type: InquiryPackQuestionType;
  label: string;
  /** Sub-label under a checkbox's label ("Will there be live band(s)
   *  performing?"). Checkbox questions only — a text/select question uses
   *  `hint`, which renders under the input. */
  description?: string;
  hint?: string;
  required?: boolean;
  /** Suppresses the question once a document is uploaded, because the parse
   *  answers it more reliably than the customer would. */
  hiddenWhenTechRiderFile?: boolean;
  /** The conditions it is asked under, all of which must hold. Absent or
   *  empty is a question always asked. */
  when?: readonly InquiryPackQuestionCondition[];
  /** Required (non-empty) for `select`, forbidden otherwise. */
  options?: readonly string[];
}

/**
 * The answers a condition may name on this question: yes/no for a
 * checkbox, its options for a select, none for anything else (a text or
 * number answer is nothing another question can wait on).
 */
export function possibleAnswers(
  question: Pick<InquiryPackQuestionConfig, "type" | "options">,
): readonly string[] {
  if (question.type === "checkbox") return ["yes", "no"];
  if (question.type === "select") return question.options ?? [];
  return [];
}

/**
 * The Company's colours (migration 042): the surface its form sits on and
 * the accent it carries. Both are names the stylesheet knows a ladder for
 * (`.dark`, `.accent-red` in styles.css) — never a hex, so the wire cannot
 * carry a colour nobody checked for contrast. Absent is the product's own
 * look: a white page with the green accent.
 */
export type InquiryFormScheme = "light" | "dark";
export type InquiryFormAccent = "green" | "red";
export interface InquiryFormTheme {
	scheme: InquiryFormScheme;
	accent: InquiryFormAccent;
}

export interface InquiryProfileConfig {
	company?: {
		/** The Company's own label: what the form's analytics name it by. */
		slug?: string;
		name: string;
		contactEmail?: string;
		publicUrl?: string;
		theme?: InquiryFormTheme;
	};
	/** Server-injected (like `company`): whether the Estimate is asked
	 *  for at the review gate (ADR-0030). Absent = disabled. */
	estimate?: { enabled: boolean };
	/** Server-injected: whether the venue step asks for a site visit before
	 *  the quote (migration 053). Absent = offered, as every profile did
	 *  before the Company could say otherwise. */
	siteVisit?: { offered: boolean };
	/** Server-injected: whether intake drafts a quote for this Company
	 *  (#273). Off, the confirmation screen promises no quote by email.
	 *  Absent = drafted, as every Company's was before it could say. */
	quoting?: { drafted: boolean };
	/** Server-injected: the Company's published call-in number for this
	 *  profile, E.164. Absent = the Company has no number, so the entry
	 *  screen offers no call row. */
	phone?: { e164: string };
  key: InquiryProfileKey;
	schemaVersion: number;
  label: string;
  initialStep: Step;
  initialFormData: Partial<PackageFormData>;
	entryFlow: {
		title: string;
		description: string;
		quickEnquiry: {
			enabled: boolean;
			label: string;
			description: string;
		};
		fullBrief: {
			enabled: boolean;
			label: string;
			description: string;
		};
		dryHire: {
			enabled: boolean;
			label: string;
			description: string;
		};
	};
  steps: readonly InquiryStepConfig[];
  documentsStep: {
    title: string;
    description: string;
    yesLabel: string;
    noLabel: string;
    uploadAriaLabel: string;
    uploadPrompt: string;
    acceptedFormatsLabel: string;
    missingFileMessage: string;
    parseFailedTitle: string;
    parseFailedMessage: string;
    noDocumentMessage: string;
  };
  eventDetailsStep: {
    title: string;
    fields: {
      eventType: InquiryFieldConfig<"eventType">;
      eventTypeOther: InquiryFieldConfig<"eventTypeOther">;
      eventName: InquiryFieldConfig<"eventName">;
      organization: InquiryFieldConfig<"organization">;
      eventDate: InquiryFieldConfig<"eventDate">;
      eventEndDate: InquiryFieldConfig<"eventEndDate">;
      eventStartTime: InquiryFieldConfig<"eventStartTime">;
      eventEndTime: InquiryFieldConfig<"eventEndTime">;
    };
    eventTypeOptions: readonly EventTypeOption[];
  };
  contactStep: {
    title: string;
    fields: {
      contactName: InquiryFieldConfig<"contactName">;
      contactEmail: InquiryFieldConfig<"contactEmail">;
      contactPhone: InquiryFieldConfig<"contactPhone">;
      preferredContactMethod: InquiryFieldConfig<"preferredContactMethod">;
      contactWhen: InquiryFieldConfig<"contactWhen">;
    };
  };
  venueStep: {
    title: string;
    scale: InquiryScaleFieldConfig;
    fields: {
      location: InquiryFieldConfig<"location">;
      venueContact: InquiryFieldConfig<"venueContact">;
      environment: {
        label: string;
        options: readonly string[];
        emptyMessage: string;
      };
    };
    outdoor: {
      when: { field: "indoorOutdoor"; equals: string };
      powerAccess: InquiryFieldConfig<"powerAccess"> & {
        emptyMessage: string;
      };
      wetWeatherPlan: InquiryFieldConfig<"wetWeatherPlan">;
    };
    stage: {
      available: InquiryCheckboxQuestionConfig<"hasStage">;
      details: InquiryFieldConfig<"stageDetails">;
    };
    siteVisit: {
      available: InquiryCheckboxQuestionConfig<"siteVisit">;
      when: InquiryFieldConfig<"siteVisitWhen">;
    };
    timing: {
      title: string;
      description: string;
      invalidTimeMessage: string;
      siteAvailableFrom: InquiryFieldConfig<"siteAvailableFrom">;
      packOutTime: InquiryFieldConfig<"packOutTime">;
    };
  };
  contentStep: {
    title: string;
    /** Discipline word for the stepper + "Continue to …" label — the one
     *  step whose name genuinely varies per Profile ("Staging", "Lighting").
     *  Absent = the app-constant "Content". */
    progressLabel?: string;
    autofillNotice: string;
    /** The profile's content questions. Every profile is data-driven —
     *  live_sound's fixed trio moved here in the monorepo's migration 095, which is what
     *  lets one document parser serve every profile. */
    packQuestions: readonly InquiryPackQuestionConfig[];
    additionalInfo: InquiryFieldConfig<"additionalInfo">;
  };
}

const FORM_SCHEMES: readonly InquiryFormScheme[] = ["light", "dark"];
const FORM_ACCENTS: readonly InquiryFormAccent[] = ["green", "red"];

function parseInquiryFormTheme(value: unknown): InquiryFormTheme | undefined {
	if (!isRecord(value)) return undefined;
	const scheme = FORM_SCHEMES.find((s) => s === value.scheme);
	const accent = FORM_ACCENTS.find((a) => a === value.accent);
	if (scheme === undefined || accent === undefined) return undefined;
	return { scheme, accent };
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function matchesTemplate(
	value: unknown,
	template: unknown,
	property = "",
): boolean {
	if (Array.isArray(template)) {
		if (!Array.isArray(value)) return false;
		if (template.length === 0) return true;
		return value.every((item) => matchesTemplate(item, template[0]));
	}
	if (isRecord(template)) {
		if (!isRecord(value)) return false;
		return Object.entries(template).every(([key, childTemplate]) => {
			const childValue = value[key];
			if (childTemplate === undefined) return childValue === undefined;
			return matchesTemplate(childValue, childTemplate, key);
		});
	}
	if (property === "field") {
		return value === template;
	}
	return typeof value === typeof template;
}

/**
 * The shape template a stored row must match before parsing continues.
 * `matchesTemplate` compares by `typeof`, so the string/number values here
 * are placeholders — except `field` properties, which compare by equality
 * (hence the literal "additionalInfo").
 */
const storedConfigTemplate = {
	key: "",
	schemaVersion: 0,
	label: "",
	initialStep: "",
	initialFormData: {},
	documentsStep: {
		title: "",
		description: "",
		yesLabel: "",
		noLabel: "",
		uploadAriaLabel: "",
		uploadPrompt: "",
		acceptedFormatsLabel: "",
		missingFileMessage: "",
		parseFailedTitle: "",
		parseFailedMessage: "",
		noDocumentMessage: "",
	},
	contentStep: {
		title: "",
		autofillNotice: "",
		additionalInfo: { field: "additionalInfo", label: "" },
	},
};

const PACK_QUESTION_ID_PATTERN = /^[a-z][a-zA-Z0-9_]{0,63}$/;
const PACK_QUESTION_TYPES: ReadonlySet<string> = new Set([
	"checkbox",
	"integer",
	"text",
	"textarea",
	"select",
]);
// The one cap, the server's profile.MaxQuestions (#80): twenty sentences.
// The server's `profile.MaxQuestions`: one flat list, every line counted.
const MAX_PACK_QUESTIONS = 20;
const MAX_PACK_LABEL_LENGTH = 200;
const MAX_PACK_HINT_LENGTH = 500;
const MAX_PACK_OPTIONS = 20;
const MAX_PACK_OPTION_LENGTH = 100;

function parsePackQuestion(
	value: unknown,
	earlier: ReadonlyMap<string, InquiryPackQuestionConfig>,
): InquiryPackQuestionConfig | null {
	if (!isRecord(value)) return null;
	const {
		id,
		type,
		label,
		description,
		hint,
		required,
		hiddenWhenTechRiderFile,
		when,
		options,
	} = value;
	if (
		typeof id !== "string" ||
		!PACK_QUESTION_ID_PATTERN.test(id) ||
		earlier.has(id) ||
		typeof type !== "string" ||
		!PACK_QUESTION_TYPES.has(type) ||
		typeof label !== "string" ||
		label.length === 0 ||
		label.length > MAX_PACK_LABEL_LENGTH
	) {
		return null;
	}
	if (
		(hint !== undefined &&
			(typeof hint !== "string" ||
				hint.length === 0 ||
				hint.length > MAX_PACK_HINT_LENGTH)) ||
		(description !== undefined &&
			(typeof description !== "string" ||
				description.length === 0 ||
				description.length > MAX_PACK_HINT_LENGTH)) ||
		(required !== undefined && typeof required !== "boolean") ||
		(hiddenWhenTechRiderFile !== undefined &&
			typeof hiddenWhenTechRiderFile !== "boolean")
	) {
		return null;
	}
	// A condition names a core answer or a question above this one, and an
	// answer that question can give. One that names anything else is
	// dropped rather than failing the pack: the server holds the list to
	// the same rule, so a stray one is a list from before it, and a
	// question with a condition nothing can meet is better asked than lost.
	let parsedWhen: InquiryPackQuestionCondition[] | undefined;
	if (when !== undefined) {
		if (!Array.isArray(when) || when.length > MAX_PACK_CONDITIONS) return null;
		parsedWhen = [];
		for (const item of when) {
			if (
				!isRecord(item) ||
				typeof item.field !== "string" ||
				typeof item.equals !== "string" ||
				item.equals.length === 0 ||
				item.equals.length > MAX_PACK_OPTION_LENGTH
			) {
				return null;
			}
			if (item.field === CORE_CONDITION_FIELD) {
				parsedWhen.push({ field: item.field, equals: item.equals });
				continue;
			}
			const source = earlier.get(item.field);
			if (!source || !possibleAnswers(source).includes(item.equals)) continue;
			parsedWhen.push({ field: item.field, equals: item.equals });
		}
	}
	let parsedOptions: readonly string[] | undefined;
	if (type === "select") {
		if (
			!Array.isArray(options) ||
			options.length === 0 ||
			options.length > MAX_PACK_OPTIONS ||
			options.some(
				(option) =>
					typeof option !== "string" ||
					option.length === 0 ||
					option.length > MAX_PACK_OPTION_LENGTH,
			) ||
			new Set(options).size !== options.length
		) {
			return null;
		}
		parsedOptions = options as string[];
	} else if (options !== undefined) {
		return null;
	}
	return {
		id,
		type: type as InquiryPackQuestionType,
		label,
		...(description !== undefined ? { description } : {}),
		...(hint !== undefined ? { hint } : {}),
		...(required !== undefined ? { required } : {}),
		...(hiddenWhenTechRiderFile !== undefined
			? { hiddenWhenTechRiderFile }
			: {}),
		...(parsedWhen && parsedWhen.length > 0 ? { when: parsedWhen } : {}),
		...(parsedOptions ? { options: parsedOptions } : {}),
	};
}

export function parsePackQuestions(
	value: unknown,
): readonly InquiryPackQuestionConfig[] | null {
	// An empty list is a form that asks only the core steps: the server
	// sends [] for a Profile whose Company removed every question.
	if (!Array.isArray(value) || value.length > MAX_PACK_QUESTIONS) {
		return null;
	}
	const earlier = new Map<string, InquiryPackQuestionConfig>();
	const questions: InquiryPackQuestionConfig[] = [];
	for (const item of value) {
		const question = parsePackQuestion(item, earlier);
		if (!question) return null;
		earlier.set(question.id, question);
		questions.push(question);
	}
	return questions;
}

/**
 * Extract an entry-flow enabled flag. An absent `entryFlow`/section falls
 * back to `defaultEnabled`: true for the always-on paths (the generic wording
 * is constant, so a minimal config need not carry the key at all), false for
 * opt-in dryHire — matching the Go zero-value behaviour in
 * validateInquiryPathAgainstProfile. A present flag must be a boolean.
 * Returns null on a malformed key.
 */
function entryFlowEnabled(
	value: Record<string, unknown>,
	flow: "quickEnquiry" | "fullBrief" | "dryHire",
	defaultEnabled: boolean,
): boolean | null {
	const entryFlow = value.entryFlow;
	if (entryFlow === undefined) return defaultEnabled;
	if (!isRecord(entryFlow)) return null;
	const section = entryFlow[flow];
	if (section === undefined) return defaultEnabled;
	if (!isRecord(section) || typeof section.enabled !== "boolean") return null;
	return section.enabled;
}

/**
 * The Profile's own event types, server-injected beside `estimate` (#197):
 * `[{key, label}]`, Other last. Tolerant like the estimate flag — a
 * malformed entry is dropped, never a reason to lose the form — and Other
 * is put last whatever arrived, because it is on every list and it is
 * what opens "Please specify".
 */
export function parseEventTypeOptions(value: unknown): EventTypeOption[] {
	const out: EventTypeOption[] = [];
	const seen = new Set<string>([OTHER_EVENT_TYPE]);
	if (Array.isArray(value)) {
		for (const entry of value) {
			if (!isRecord(entry)) continue;
			const { key, label } = entry;
			if (typeof key !== "string" || typeof label !== "string") continue;
			if (!key.trim() || !label.trim() || seen.has(key)) continue;
			seen.add(key);
			out.push({ value: key, label: label.trim() });
		}
	}
	return [...out, otherEventTypeOption];
}

/**
 * Validate the tenant-owned public profile before it can render.
 *
 * A profile row carries only what varies per discipline: identity
 * (key/label), the content step, entry-flow enabled
 * flags, and initial form data. The generic sections (entry-flow wording,
 * step list, documents/event-details/contact/venue steps) are app constants
 * composed in here — stored copies of those keys are ignored, so a tenant
 * row can no longer drift on uniform copy (#60). See profile-constants.ts
 * for the constants and the statement of that contract.
 *
 * Backend-only config keys ride along untouched: `matchesTemplate` ignores
 * unknown keys, so e.g. `documentsStep.extraction.contextHints` (#58 —
 * prompt-tuning lines for pack document extraction, consumed by the Go
 * parse handler) passes through without frontend validation.
 *
 * The content step is one shape for every profile: `packQuestions`, a
 * data-driven question pack (schemaVersion 3). live_sound's fixed trio was
 * the last holdout and moved there in the monorepo's migration 095; a row still carrying
 * the old `contentStep.questions` fails this parse.
 */
export function parseInquiryProfileConfig(
	value: unknown,
): InquiryProfileConfig | null {
	if (!matchesTemplate(value, storedConfigTemplate) || !isRecord(value)) {
		return null;
	}
	const candidate = value as unknown as Pick<
		InquiryProfileConfig,
		| "key"
		| "schemaVersion"
		| "label"
		| "initialStep"
		| "initialFormData"
		| "documentsStep"
	> & { contentStep: InquiryProfileConfig["contentStep"] };
	if (
		!/^[a-z0-9][a-z0-9_]*$/.test(candidate.key) ||
		candidate.schemaVersion !== 3
	) {
		return null;
	}
	const contentStep = candidate.contentStep;
	// Per-discipline stepper word for the content step ("Staging",
	// "Lighting"). Malformed values fall back to the constant "Content"
	// rather than rejecting the profile — it's display copy, not structure.
	const contentProgressLabel =
		typeof contentStep.progressLabel === "string" &&
		contentStep.progressLabel.length > 0 &&
		contentStep.progressLabel.length <= 24
			? contentStep.progressLabel
			: undefined;
	const packQuestions = parsePackQuestions(contentStep.packQuestions);
	if (!packQuestions) return null;
	const parsedContentStep: InquiryProfileConfig["contentStep"] = {
		title: contentStep.title,
		...(contentProgressLabel ? { progressLabel: contentProgressLabel } : {}),
		autofillNotice: contentStep.autofillNotice,
		packQuestions,
		additionalInfo: contentStep.additionalInfo,
	};
	if (!constantSteps.some((step) => step.id === candidate.initialStep)) {
		return null;
	}
	const quickEnquiryEnabled = entryFlowEnabled(value, "quickEnquiry", true);
	const fullBriefEnabled = entryFlowEnabled(value, "fullBrief", true);
	const dryHireEnabled = entryFlowEnabled(value, "dryHire", false);
	if (
		quickEnquiryEnabled === null ||
		fullBriefEnabled === null ||
		dryHireEnabled === null ||
		(!quickEnquiryEnabled && !fullBriefEnabled && !dryHireEnabled)
	) {
		return null;
	}
	let company: InquiryProfileConfig["company"];
	if (value.company !== undefined) {
		if (
			!isRecord(value.company) ||
			typeof value.company.name !== "string" ||
			(value.company.contactEmail !== undefined &&
				typeof value.company.contactEmail !== "string") ||
			(value.company.publicUrl !== undefined &&
				typeof value.company.publicUrl !== "string")
		) {
			return null;
		}
		// Built key by key rather than cast, so nothing the server did not
		// mean to send rides along. The theme is tolerant like the estimate
		// flag: a name this build has no ladder for reads as the product's
		// own look rather than costing the Customer the form.
		const theme = parseInquiryFormTheme(value.company.theme);
		company = {
			// Tolerant like the theme: it labels analytics and nothing
			// else, so a bad one is dropped rather than costing the form.
			...(typeof value.company.slug === "string" && value.company.slug
				? { slug: value.company.slug }
				: {}),
			name: value.company.name,
			...(typeof value.company.contactEmail === "string"
				? { contactEmail: value.company.contactEmail }
				: {}),
			...(typeof value.company.publicUrl === "string"
				? { publicUrl: value.company.publicUrl }
				: {}),
			...(theme !== undefined ? { theme } : {}),
		};
	}
	return {
		...(company !== undefined ? { company } : {}),
		...parseServerFlags(value),
		key: candidate.key,
		schemaVersion: candidate.schemaVersion,
		label: candidate.label,
		initialStep: candidate.initialStep,
		initialFormData: candidate.initialFormData,
		entryFlow: {
			title: constantEntryFlowCopy.title,
			description: constantEntryFlowCopy.description,
			quickEnquiry: {
				...constantEntryFlowCopy.quickEnquiry,
				enabled: quickEnquiryEnabled,
			},
			fullBrief: {
				...constantEntryFlowCopy.fullBrief,
				enabled: fullBriefEnabled,
			},
			dryHire: {
				...constantEntryFlowCopy.dryHire,
				enabled: dryHireEnabled,
			},
		},
		steps: contentProgressLabel
			? constantSteps.map((step) =>
					step.id === "content"
						? { ...step, title: contentProgressLabel, progressLabel: contentProgressLabel }
						: step,
				)
			: constantSteps,
		// Display copy is app-constant like every other uniform step (#60);
		// spread order keeps backend-only keys (documentsStep.extraction)
		// riding along from the stored row.
		documentsStep: {
			...candidate.documentsStep,
			...constantDocumentsStep,
		},
		eventDetailsStep: {
			...constantEventDetailsStep,
			eventTypeOptions: parseEventTypeOptions(value.eventTypes),
		},
		contactStep: constantContactStep,
		venueStep: constantVenueStep,
		contentStep: parsedContentStep,
	};
}

/**
 * The flags the API injects beside the stored config on the way out
 * (`inquiry_profiles.go`), one row each: the field, and the one value
 * inside it this build reads. Every one is tolerant — a malformed shape
 * reads as absent rather than rejecting the profile, because a flag is
 * never a reason to lose the form — and absent means what the field's
 * own comment on `InquiryProfileConfig` says it means.
 */
const serverFlags = {
	estimate: { key: "enabled", valid: (v: unknown) => typeof v === "boolean" },
	siteVisit: { key: "offered", valid: (v: unknown) => typeof v === "boolean" },
	quoting: { key: "drafted", valid: (v: unknown) => typeof v === "boolean" },
	phone: {
		key: "e164",
		valid: (v: unknown) => typeof v === "string" && /^\+[1-9]\d{6,14}$/.test(v),
	},
} as const;

type ServerFlags = Pick<InquiryProfileConfig, keyof typeof serverFlags>;

function parseServerFlags(value: Record<string, unknown>): ServerFlags {
	const flags: Record<string, unknown> = {};
	for (const [field, { key, valid }] of Object.entries(serverFlags)) {
		const raw = value[field];
		if (isRecord(raw) && valid(raw[key])) flags[field] = { [key]: raw[key] };
	}
	return flags as ServerFlags;
}

/**
 * Whether the confirmation screen may poll for a quote and promise one by
 * email. A Company that does not want intake to quote (#273) gets back to
 * the Customer itself, so the screen says only that the enquiry arrived.
 */
export function quoteIsDrafted(profile: Pick<InquiryProfileConfig, "quoting">): boolean {
	return profile.quoting?.drafted !== false;
}
